import React, { useState } from "react";
import { changeAdminPasswordAPI, getUserData } from "../../utils/api";
import { showSuccess } from "../../utils/sweetAlert";
import { ErrorAlert } from "../../components/ui";
import "./Settings.css";

const FIELDS = [
  { name: "currentPassword", label: "Current password", autoComplete: "current-password" },
  { name: "newPassword", label: "New password", autoComplete: "new-password" },
  { name: "confirmPassword", label: "Confirm new password", autoComplete: "new-password" },
];

const getStrength = (password) => {
  if (!password) return { score: 0, label: "", tone: "" };
  let score = 0;
  if (password.length >= 6) score += 1;
  if (password.length >= 10) score += 1;
  if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score += 1;
  if (/\d/.test(password)) score += 1;
  if (/[^A-Za-z0-9]/.test(password)) score += 1;
  if (password.length < 6) return { score: 1, label: "Too short", tone: "weak" };
  if (score <= 2) return { score: 2, label: "Fair", tone: "fair" };
  if (score <= 3) return { score: 3, label: "Good", tone: "good" };
  return { score: 4, label: "Strong", tone: "strong" };
};

function Settings() {
  const userData = getUserData();
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showPassword, setShowPassword] = useState({});

  const strength = getStrength(passwordData.newPassword);
  const confirmMatches =
    passwordData.confirmPassword.length > 0 && passwordData.confirmPassword === passwordData.newPassword;

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
    if (formError) setFormError("");
  };

  const validate = () => {
    const next = {};
    if (!passwordData.currentPassword) next.currentPassword = "Enter your current password.";
    if (!passwordData.newPassword) next.newPassword = "Enter a new password.";
    else if (passwordData.newPassword.length < 6) next.newPassword = "Use at least 6 characters.";
    else if (passwordData.newPassword === passwordData.currentPassword)
      next.newPassword = "The new password must be different from the current one.";
    if (!passwordData.confirmPassword) next.confirmPassword = "Re-enter the new password.";
    else if (passwordData.confirmPassword !== passwordData.newPassword)
      next.confirmPassword = "Passwords don't match.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    if (!validate() || passwordLoading) return;

    try {
      setPasswordLoading(true);
      const response = await changeAdminPasswordAPI({
        oldPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
        confirmPassword: passwordData.confirmPassword,
      });

      if (response?.success) {
        showSuccess(response.message || "Use your new password next time you sign in.", "Password changed");
        setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
        setShowPassword({});
      } else if (response) {
        setFormError(response.message || "Couldn't change your password.");
      }
    } catch (error) {
      console.error("Error changing password:", error);
      setFormError(error.message || "Couldn't change your password. Please try again.");
    } finally {
      setPasswordLoading(false);
    }
  };

  const initials =
    (userData?.name || "Admin")
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0])
      .join("") || "A";

  return (
    <div className="settings-page page">
      <div className="page-header">
        <div>
          <h2>Settings</h2>
          <p>Your admin profile and account security.</p>
        </div>
      </div>

      <div className="settings-layout">
        <aside className="card settings-profile">
          <div className="settings-profile-cover"></div>
          <span className="avatar settings-avatar">{initials}</span>
          <h3>{userData?.name || "Administrator"}</h3>
          <p>{userData?.email || "—"}</p>
          <span className="badge badge-brand">
            <i className="bi bi-shield-check"></i> {userData?.role ? String(userData.role) : "Admin"}
          </span>
        </aside>

        <section className="card">
          <div className="card-header">
            <div className="settings-card-title">
              <span className="settings-card-icon">
                <i className="bi bi-shield-lock"></i>
              </span>
              <div>
                <h3>Change password</h3>
                <p className="text-muted">Choose a strong password you don't use anywhere else.</p>
              </div>
            </div>
          </div>

          <form onSubmit={handlePasswordSubmit} className="card-body settings-form" noValidate>
            <ErrorAlert message={formError} onDismiss={() => setFormError("")} />

            {FIELDS.map((field) => (
              <div className="form-group" key={field.name}>
                <label htmlFor={field.name}>
                  {field.label} <span className="required">*</span>
                </label>
                <div className="input-icon settings-password">
                  <i className="bi bi-lock"></i>
                  <input
                    type={showPassword[field.name] ? "text" : "password"}
                    id={field.name}
                    name={field.name}
                    value={passwordData[field.name]}
                    onChange={handlePasswordChange}
                    autoComplete={field.autoComplete}
                    className={errors[field.name] ? "error" : ""}
                    disabled={passwordLoading}
                  />
                  <button
                    type="button"
                    className="settings-eye"
                    onClick={() => setShowPassword((prev) => ({ ...prev, [field.name]: !prev[field.name] }))}
                    aria-label={showPassword[field.name] ? "Hide password" : "Show password"}
                    tabIndex={-1}
                  >
                    <i className={`bi ${showPassword[field.name] ? "bi-eye-slash" : "bi-eye"}`}></i>
                  </button>
                </div>

                {field.name === "newPassword" && passwordData.newPassword && (
                  <div className={`strength strength-${strength.tone}`}>
                    <div className="strength-bars">
                      {[1, 2, 3, 4].map((i) => (
                        <span key={i} className={i <= strength.score ? "on" : ""}></span>
                      ))}
                    </div>
                    <span className="strength-label">{strength.label}</span>
                  </div>
                )}

                {field.name === "confirmPassword" && confirmMatches && !errors.confirmPassword && (
                  <span className="settings-match">
                    <i className="bi bi-check-circle-fill"></i> Passwords match
                  </span>
                )}

                {errors[field.name] && (
                  <span className="field-error">
                    <i className="bi bi-exclamation-circle"></i> {errors[field.name]}
                  </span>
                )}
              </div>
            ))}

            <ul className="settings-tips">
              {[
                { ok: passwordData.newPassword.length >= 6, text: "At least 6 characters" },
                {
                  ok: /[A-Z]/.test(passwordData.newPassword) && /[a-z]/.test(passwordData.newPassword),
                  text: "Upper and lower case letters",
                },
                { ok: /\d/.test(passwordData.newPassword), text: "At least one number" },
              ].map((tip) => (
                <li key={tip.text} className={tip.ok ? "ok" : ""}>
                  <i className={`bi ${tip.ok ? "bi-check-circle-fill" : "bi-circle"}`}></i> {tip.text}
                </li>
              ))}
            </ul>

            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={passwordLoading}>
                {passwordLoading ? <span className="btn-spinner"></span> : <i className="bi bi-check2"></i>}
                {passwordLoading ? "Updating…" : "Update password"}
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
}

export default Settings;
